import { makeAutoObservable, runInAction } from 'mobx';

export interface Timer {
  id: string;
  name: string;
  duration: number; // in seconds
  remaining: number; // in seconds
  isRunning: boolean;
  isPaused: boolean;
  isCompleted: boolean;
}

export interface Stopwatch {
  id: string;
  name: string;
  elapsed: number; // in seconds
  isRunning: boolean;
  isPaused: boolean;
  laps: number[];
}

export class TimerStore {
  private timers: Timer[] = [];
  private stopwatches: Stopwatch[] = [];
  private intervals: Map<string, NodeJS.Timeout> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  // Timer methods
  get allTimers(): Timer[] {
    return this.timers;
  }

  createTimer(name: string, durationInSeconds: number): Timer {
    const timer: Timer = {
      id: Date.now().toString(),
      name,
      duration: durationInSeconds,
      remaining: durationInSeconds,
      isRunning: false,
      isPaused: false,
      isCompleted: false,
    };

    runInAction(() => {
      this.timers.push(timer);
    });

    return timer;
  }

  startTimer(timerId: string): void {
    const timer = this.timers.find(t => t.id === timerId);
    if (!timer || timer.isCompleted || timer.remaining <= 0) return;

    runInAction(() => {
      timer.isRunning = true;
      timer.isPaused = false;
    });

    const interval = setInterval(() => {
      runInAction(() => {
        if (timer.remaining > 0) {
          timer.remaining -= 1;
        } else {
          timer.isRunning = false;
          timer.isCompleted = true;
          this.intervals.delete(timerId);
          clearInterval(interval);
          this.onTimerComplete(timer);
        }
      });
    }, 1000);

    this.intervals.set(timerId, interval);
  }

  pauseTimer(timerId: string): void {
    const timer = this.timers.find(t => t.id === timerId);
    if (!timer || !timer.isRunning) return;

    runInAction(() => {
      timer.isRunning = false;
      timer.isPaused = true;
    });

    const interval = this.intervals.get(timerId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(timerId);
    }
  }

  resetTimer(timerId: string): void {
    const timer = this.timers.find(t => t.id === timerId);
    if (!timer) return;

    const interval = this.intervals.get(timerId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(timerId);
    }

    runInAction(() => {
      timer.remaining = timer.duration;
      timer.isRunning = false;
      timer.isPaused = false;
      timer.isCompleted = false;
    });
  }

  deleteTimer(timerId: string): void {
    const interval = this.intervals.get(timerId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(timerId);
    }

    runInAction(() => {
      this.timers = this.timers.filter(t => t.id !== timerId);
    });
  }

  // Stopwatch methods
  get allStopwatches(): Stopwatch[] {
    return this.stopwatches;
  }

  createStopwatch(name: string): Stopwatch {
    const stopwatch: Stopwatch = {
      id: Date.now().toString(),
      name,
      elapsed: 0,
      isRunning: false,
      isPaused: false,
      laps: [],
    };

    runInAction(() => {
      this.stopwatches.push(stopwatch);
    });

    return stopwatch;
  }

  startStopwatch(stopwatchId: string): void {
    const stopwatch = this.stopwatches.find(s => s.id === stopwatchId);
    if (!stopwatch) return;

    runInAction(() => {
      stopwatch.isRunning = true;
      stopwatch.isPaused = false;
    });

    const interval = setInterval(() => {
      runInAction(() => {
        stopwatch.elapsed += 1;
      });
    }, 1000);

    this.intervals.set(stopwatchId, interval);
  }

  pauseStopwatch(stopwatchId: string): void {
    const stopwatch = this.stopwatches.find(s => s.id === stopwatchId);
    if (!stopwatch || !stopwatch.isRunning) return;

    runInAction(() => {
      stopwatch.isRunning = false;
      stopwatch.isPaused = true;
    });

    const interval = this.intervals.get(stopwatchId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(stopwatchId);
    }
  }

  resetStopwatch(stopwatchId: string): void {
    const stopwatch = this.stopwatches.find(s => s.id === stopwatchId);
    if (!stopwatch) return;

    const interval = this.intervals.get(stopwatchId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(stopwatchId);
    }

    runInAction(() => {
      stopwatch.elapsed = 0;
      stopwatch.isRunning = false;
      stopwatch.isPaused = false;
      stopwatch.laps = [];
    });
  }

  lapStopwatch(stopwatchId: string): void {
    const stopwatch = this.stopwatches.find(s => s.id === stopwatchId);
    if (!stopwatch || !stopwatch.isRunning) return;

    runInAction(() => {
      stopwatch.laps.push(stopwatch.elapsed);
    });
  }

  deleteStopwatch(stopwatchId: string): void {
    const interval = this.intervals.get(stopwatchId);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(stopwatchId);
    }

    runInAction(() => {
      this.stopwatches = this.stopwatches.filter(s => s.id !== stopwatchId);
    });
  }

  // Utility methods
  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private onTimerComplete(timer: Timer): void {
    // Could add notification sound or browser notification here
    console.log(`Timer "${timer.name}" completed!`);
  }

  // Clean up all intervals when store is destroyed
  destroy(): void {
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals.clear();
  }
}

export const timerStore = new TimerStore();
